export const COLUMN_WIDTHS_STORAGE_VERSION = 1

export interface ColumnWidthDefinition {
  id: string
  defaultWidth: number
  minWidth: number
  maxWidth: number
}

export type ColumnWidths = Record<string, number>

interface StoredColumnWidths {
  version: number
  widths: ColumnWidths
}

export function clampColumnWidth(
  definition: ColumnWidthDefinition,
  width: number,
): number {
  if (!Number.isFinite(width)) return definition.defaultWidth
  return Math.min(definition.maxWidth, Math.max(definition.minWidth, Math.round(width)))
}

export function defaultColumnWidths(
  definitions: readonly ColumnWidthDefinition[],
): ColumnWidths {
  return Object.fromEntries(
    definitions.map((definition) => [definition.id, definition.defaultWidth]),
  )
}

export function normalizeColumnWidths(
  widths: unknown,
  definitions: readonly ColumnWidthDefinition[],
): ColumnWidths {
  const candidate =
    typeof widths === "object" && widths !== null
      ? (widths as Record<string, unknown>)
      : {}

  return Object.fromEntries(
    definitions.map((definition) => {
      const width = candidate[definition.id]
      return [
        definition.id,
        typeof width === "number"
          ? clampColumnWidth(definition, width)
          : definition.defaultWidth,
      ]
    }),
  )
}

export function serializeColumnWidths(widths: ColumnWidths): string {
  return JSON.stringify({
    version: COLUMN_WIDTHS_STORAGE_VERSION,
    widths,
  } satisfies StoredColumnWidths)
}

export function deserializeColumnWidths(
  value: string | null,
  definitions: readonly ColumnWidthDefinition[],
): ColumnWidths {
  if (!value) return defaultColumnWidths(definitions)

  try {
    const stored = JSON.parse(value) as Partial<StoredColumnWidths>
    if (stored.version !== COLUMN_WIDTHS_STORAGE_VERSION) {
      return defaultColumnWidths(definitions)
    }

    return normalizeColumnWidths(stored.widths, definitions)
  } catch {
    return defaultColumnWidths(definitions)
  }
}
